import React, { useEffect, useState } from 'react';
import styles from './Landing.module.css';
import { useOktaAuth } from '@okta/okta-react';
import Header from '../../components/header/Header';
import Circle from '../../components/circle/Circle';
import OnCallUserInfo from '../../components/onCallUserInfo/OnCallUserInfo';
import ViewIncidentDetails from '../../components/viewIncidentDetails/ViewIncidentDetails';
import IncidentList from '../../components/incidentList/IncidentList';
import PagerDutyAppInfo from '../../components/pagerDutyAppInfo/PagerDutyAppInfo';
import {
  getPagerdutyToken,
  postPagerDutyOnCallEngineer,
  postPagerDutyOnGoingIncidentList,
  postPagerDutyCreateIncident,
  IOnCallEngineerResponse,
  IIncidentListResponse
} from '../../apiCall';
import { trackPromise } from 'react-promise-tracker';
import { Helmet } from 'react-helmet';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Landing = () => {
  const [onCallEngineerResponse, setOnCallEngineerResponse] = useState<IOnCallEngineerResponse>();
  const [incidentListResponse, setIncidentListResponse] = useState<IIncidentListResponse>();
  const { oktaAuth, authState } = useOktaAuth();
  const [redirected, setRedirected] = useState(false);

  //declare constant strings
  const PAGERDUTY_TOKEN = 'pagerDuty-token';


  // Function to refresh Okta token if expired
  const refreshOktaToken = async () => {
    try {
      const accessToken = await oktaAuth.tokenManager.get('accessToken');

      if (accessToken && accessToken.expiresAt < Date.now() / 1000) {
        // If token is expired, re-authenticate by redirecting
        await oktaAuth.signInWithRedirect();
      }
    } catch (error) {
      toast.error('Error checking Okta token: ' + (error as Error).message, {
        className: styles.toastContentSize
      });
    }
  };

  // Redirect to Okta login if not authenticated
  useEffect(() => {
    const redirectToLogin = async () => {
      if (authState && !authState.isAuthenticated && !redirected) {
        setRedirected(true); // Prevent further redirects
        try {
          await oktaAuth.signInWithRedirect();
        } catch (error) {
          toast.error((error as Error).message, { className: styles.toastContentSize });
        }
      }
    };

    redirectToLogin().catch((error) => {
      toast.error('Error during redirect: ' + error.message, {
        className: styles.toastContentSize
      });
    });
  }, [authState, oktaAuth, redirected]);

  const fetchTokenAndDataHandler = async () => {
    try {
      const pagerTokenDetail = await trackPromise(getPagerdutyToken());
      localStorage.setItem(PAGERDUTY_TOKEN, JSON.stringify(pagerTokenDetail));
      await fetchOnCallAndIncidentsHandler();
    } catch (error) {
      toast.error((error as Error).message, {
        className: styles.toastContentSize
      });
    }
  };

  const fetchOnCallAndIncidentsHandler = async () => {
    const storedValue = JSON.parse(localStorage.getItem(PAGERDUTY_TOKEN) || 'null');
    if (storedValue) {
      try {
        const [onCallEngineerDetails, incidentList] = await Promise.all([
          trackPromise(postPagerDutyOnCallEngineer(storedValue.tokenDetail.access_token)),
          trackPromise(postPagerDutyOnGoingIncidentList(storedValue.tokenDetail.access_token))
        ]);
        setOnCallEngineerResponse(onCallEngineerDetails);
        setIncidentListResponse(incidentList);
      } catch (error) {
        toast.error((error as Error).message, {
          className: styles.toastContentSize
        });
      }
    } else {
      setOnCallEngineerResponse({ userList: [], responseCode: '200', responseMessage: 'SUCCESS' });
      setIncidentListResponse({ incidentList: [], responseCode: 200, responseMessage: 'SUCCESS' });
    }
  };

  const createPagerdutyIncidentHandler = async () => {
    const storedValue = JSON.parse(localStorage.getItem(PAGERDUTY_TOKEN) || 'null');
    if (storedValue) {
      await trackPromise(
        postPagerDutyCreateIncident(storedValue.tokenDetail.access_token)
          .then(async (response) => {
            if (response.responseCode === 201) {
              toast.success('Incident created successfully', {
                className: styles.toastContentSize
              });
              // Refetch on-call engineer and incident list after creating an incident
              await fetchOnCallAndIncidentsHandler();
            }
          })
          .catch((error) =>
            toast.error(error.message, {
              className: styles.toastContentSize
            })
          )
      );
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      if (authState?.isAuthenticated) {
        await refreshOktaToken(); // Refresh Okta token if necessary
        await fetchTokenAndDataHandler(); // Proceed with fetching PagerDuty data
      }
    };

    if (authState?.isAuthenticated) {
      fetchInitialData().catch((error) => {
        toast.error('Error fetching initial data: ' + error.message, {
          className: styles.toastContentSize
        });
      });
    }
  }, [authState]);

  return (
    <>
      <Helmet>
        <title>{`Crisis Panic Button`}</title>
      </Helmet>
      {authState?.isAuthenticated ? (
        <>
          <Header />
          <Circle callPagerDutyCreateIncidentHandler={createPagerdutyIncidentHandler} />
          <OnCallUserInfo userName={onCallEngineerResponse?.userList[0] || null} />
          <ViewIncidentDetails />
          <PagerDutyAppInfo />
          <IncidentList incidentList={incidentListResponse?.incidentList || []} />
        </>
      ) : (
        <div></div>
      )}
      <div className={styles.emptyDiv} />
      <ToastContainer />
    </>
  );
};

export default Landing;
